import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Loader,
  Textarea,
  toast,
} from "@devhop/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import DOMPurify, { type Config } from "dompurify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { publicClientNumberFieldMessage } from "@/modules/forms/domain/lib/form-number-answer";
import {
  getRepeaterClientError,
  parseRepeaterSettings,
  serializeRepeaterAnswer,
  type RepeaterAnswer,
} from "@/modules/forms/domain/lib/form-repeater-settings";
import {
  initRepeaterValsForFields,
  PublicFormRepeaterBlock,
} from "@/modules/forms/presentation/public/PublicFormRepeaterBlock";
import {
  describePublicLoadError,
  describePublicSubmitError,
  publicFormsApi,
  publicSubmitFieldMessage,
} from "@/modules/forms/presentation/public/public-forms-api";
import type {
  PublicFormFieldDTO,
  PublicFormHttpError,
  PublicFormResponseDTO,
} from "@/modules/forms/presentation/public/types";
import type { FormFieldOptionItem } from "@/server/platform/db/schema/forms";
import { config } from "@/shared/lib/config";

function mergeDuplicates(values: string[]): string[] {
  return [...new Set(values)];
}

const SECTION_RICH_HTML_OPTS: Config = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "strike",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "a",
    "blockquote",
    "code",
    "pre",
    "hr",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
};

function sanitizeSectionDescriptionHtml(html: string): string {
  return DOMPurify.sanitize(html, SECTION_RICH_HTML_OPTS);
}

function acceptAttrFromSettings(
  accept: readonly string[] | undefined,
): string | undefined {
  if (!accept?.length) return undefined;
  return accept.join(",");
}

function filePassesSize(file: File, maxBytes?: number): boolean {
  if (maxBytes == null || !Number.isFinite(maxBytes)) return true;
  return file.size <= maxBytes;
}

function FieldErrorLine({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="text-destructive text-xs" role="alert">
      {message}
    </p>
  );
}

export type PublicFormFieldBlockProps = {
  formId: string;
  field: PublicFormFieldDTO;
  disabled: boolean;
  fieldError?: string;
  clearFieldError: (fieldId: string) => void;
  setFieldError: (fieldId: string, message: string) => void;
  textVals: Record<string, string>;
  setTextVals: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  checkVals: Record<string, string[]>;
  setCheckVals: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  fileVals: Record<string, string>;
  setFileVals: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  fileBusy: Record<string, boolean>;
  setFileBusy: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  repeaterVals: Record<string, RepeaterAnswer>;
  setRepeaterVals: React.Dispatch<
    React.SetStateAction<Record<string, RepeaterAnswer>>
  >;
  uploadFile?: (formId: string, fieldId: string, file: File) => Promise<string>;
};

export function PublicFormFieldBlock({
  formId,
  field,
  disabled,
  fieldError,
  clearFieldError,
  setFieldError,
  textVals,
  setTextVals,
  checkVals,
  setCheckVals,
  fileVals,
  setFileVals,
  fileBusy,
  setFileBusy,
  repeaterVals,
  setRepeaterVals,
  uploadFile = publicFormsApi.uploadFileViaPresign,
}: PublicFormFieldBlockProps) {
  const id = `pf-${field.id}`;
  const val = textVals[field.id] ?? "";
  const checks = checkVals[field.id] ?? [];
  const maxRating =
    field.type === "rating" &&
    field.settings &&
    typeof field.settings === "object" &&
    field.settings.maxRating === 10
      ? 10
      : 5;

  if (field.type === "section") {
    const raw = field.description?.trim() ?? "";
    const safe = raw.length ? sanitizeSectionDescriptionHtml(raw) : "";
    return (
      <div className="space-y-2 border-border border-t pt-4 first:border-t-0 first:pt-0">
        <h2 className="font-semibold text-foreground text-lg">{field.label}</h2>
        {safe ? (
          <div
            className="max-w-none text-muted-foreground text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_blockquote]:border-border [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_h1]:mt-4 [&_h1]:font-semibold [&_h1]:text-base [&_h1]:text-foreground [&_h2]:mt-3 [&_h2]:font-semibold [&_h2]:text-base [&_h2]:text-foreground [&_h3]:mt-2 [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:text-sm [&_h4]:mt-2 [&_h4]:font-semibold [&_h4]:text-foreground [&_h4]:text-sm [&_h5]:mt-2 [&_h5]:font-medium [&_h5]:text-foreground [&_h5]:text-sm [&_h6]:mt-2 [&_h6]:font-medium [&_h6]:text-foreground [&_h6]:text-xs [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
            // HTML ຈາກ admin (Tiptap) ຜ່ານ DOMPurify ກ່ອນສະແດງ
            // biome-ignore lint/security/noDangerouslySetInnerHtml: ເນື້ອຫາຖືກຈຳກັດແທັກ/ແອັດທິບຜ່ານ DOMPurify ກ່ອນແສດງ
            dangerouslySetInnerHTML={{ __html: safe }}
          />
        ) : null}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <Textarea
          id={id}
          rows={
            typeof field.settings?.rows === "number" ? field.settings.rows : 4
          }
          disabled={disabled}
          placeholder={field.placeholder ?? undefined}
          value={val}
          onChange={(e) => {
            clearFieldError(field.id);
            setTextVals((p) => ({ ...p, [field.id]: e.target.value }));
          }}
          className="min-h-20"
        />
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "text") {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <Input
          id={id}
          disabled={disabled}
          placeholder={field.placeholder ?? undefined}
          value={val}
          onChange={(e) => {
            clearFieldError(field.id);
            setTextVals((p) => ({ ...p, [field.id]: e.target.value }));
          }}
        />
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "number") {
    const st = field.settings;
    const numMin =
      typeof st?.min === "number" && Number.isFinite(st.min)
        ? st.min
        : undefined;
    const numMax =
      typeof st?.max === "number" && Number.isFinite(st.max)
        ? st.max
        : undefined;
    const integerOnly = st?.integerOnly === true;
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <Input
          id={id}
          type="number"
          inputMode={integerOnly ? "numeric" : "decimal"}
          step={integerOnly ? 1 : "any"}
          min={numMin}
          max={numMax}
          disabled={disabled}
          placeholder={field.placeholder ?? undefined}
          value={val}
          onChange={(e) => {
            clearFieldError(field.id);
            setTextVals((p) => ({ ...p, [field.id]: e.target.value }));
          }}
          className="tabular-nums"
        />
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "date") {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <Input
          id={id}
          type="date"
          disabled={disabled}
          value={val}
          onChange={(e) => {
            clearFieldError(field.id);
            setTextVals((p) => ({ ...p, [field.id]: e.target.value }));
          }}
        />
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "datetime") {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <Input
          id={id}
          type="datetime-local"
          disabled={disabled}
          value={val}
          onChange={(e) => {
            clearFieldError(field.id);
            setTextVals((p) => ({ ...p, [field.id]: e.target.value }));
          }}
        />
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "choice") {
    const opts = field.options ?? [];
    return (
      <fieldset
        className="flex flex-col gap-2 border-0 p-0"
        disabled={disabled}
      >
        <legend className="mb-1 font-medium text-sm">
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </legend>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <div className="flex flex-col gap-2">
          {opts.map((o) => (
            <label
              key={o.value}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <input
                type="radio"
                className="size-4 accent-primary"
                name={field.id}
                value={o.value}
                checked={val === o.value}
                disabled={disabled}
                onChange={() => {
                  clearFieldError(field.id);
                  setTextVals((p) => ({ ...p, [field.id]: o.value }));
                }}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
        <FieldErrorLine message={fieldError} />
      </fieldset>
    );
  }

  if (field.type === "dropdown") {
    const opts = field.options ?? [];
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <select
          id={id}
          disabled={disabled}
          value={val}
          onChange={(e) => {
            clearFieldError(field.id);
            setTextVals((p) => ({ ...p, [field.id]: e.target.value }));
          }}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <option value="">—</option>
          {opts.map((o: FormFieldOptionItem) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "checkbox") {
    const opts = field.options ?? [];
    const toggle = (v: string) => {
      clearFieldError(field.id);
      setCheckVals((prev) => {
        const cur = prev[field.id] ?? [];
        const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
        return { ...prev, [field.id]: mergeDuplicates(next) };
      });
    };
    return (
      <fieldset
        className="flex flex-col gap-2 border-0 p-0"
        disabled={disabled}
      >
        <legend className="mb-1 font-medium text-sm">
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </legend>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <div className="flex flex-col gap-2">
          {opts.map((o) => {
            const chkId = `${id}-cb-${o.value}`;
            return (
              <div
                key={o.value}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <Checkbox
                  id={chkId}
                  checked={checks.includes(o.value)}
                  disabled={disabled}
                  onCheckedChange={() => toggle(o.value)}
                />
                <Label htmlFor={chkId} className="cursor-pointer font-normal">
                  {o.label}
                </Label>
              </div>
            );
          })}
        </div>
        <FieldErrorLine message={fieldError} />
      </fieldset>
    );
  }

  if (field.type === "rating") {
    return (
      <div className="flex flex-col gap-2">
        <span className="font-medium text-sm">
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </span>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: maxRating }, (_, i) => i + 1).map((n) => (
            <Button
              key={n}
              type="button"
              size="sm"
              variant={val === String(n) ? "default" : "outline"}
              disabled={disabled}
              className="min-w-10"
              onClick={() => {
                clearFieldError(field.id);
                setTextVals((p) => ({ ...p, [field.id]: String(n) }));
              }}
            >
              {n}
            </Button>
          ))}
        </div>
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "file") {
    const busy = fileBusy[field.id] ?? false;
    const fk = fileVals[field.id] ?? "";
    const maxBytes = field.settings?.maxFileSizeBytes;
    const inputSubId = `${id}-inp`;
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={inputSubId}>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        <input
          id={inputSubId}
          type="file"
          disabled={disabled || busy}
          accept={acceptAttrFromSettings(field.settings?.accept)}
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            clearFieldError(field.id);
            if (!filePassesSize(file, maxBytes)) {
              setFieldError(field.id, "ໄຟລໃຫຍ່ເກີນທີ່ກຳນົດໃນຟອມ");
              return;
            }
            setFileBusy((b) => ({ ...b, [field.id]: true }));
            try {
              const key = await uploadFile(formId, field.id, file);
              setFileVals((p) => ({ ...p, [field.id]: key }));
              toast.success("ອັບໂຫຼດໄຟລແລ້ວ");
            } catch (err) {
              const pe = err as PublicFormHttpError;
              const msg =
                pe.code === "FILE_TOO_LARGE"
                  ? "ໄຟລໃຫຍ່ເກີນທີ່ເຊີກອະນຸຍາດ"
                  : pe.code === "FILE_TYPE_NOT_ALLOWED"
                    ? "ຊະນິດໄຟລບໍ່ຢູ່ໃນລາຍການທີ່ອະນຸຍາດ"
                    : "ອັບໂຫຼດບໍ່ສຳເລັດ";
              setFieldError(field.id, msg);
            } finally {
              setFileBusy((b) => ({ ...b, [field.id]: false }));
            }
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={disabled || busy}
            onClick={() => {
              clearFieldError(field.id);
              document.getElementById(inputSubId)?.click();
            }}
          >
            {busy ? "ກຳລັງອັບໂຫຼດ…" : "ເລືອກໄຟລ"}
          </Button>
          {fk ? (
            <span className="text-muted-foreground text-xs">ພ້ອມສົ່ງແລ້ວ</span>
          ) : null}
        </div>
        <FieldErrorLine message={fieldError} />
      </div>
    );
  }

  if (field.type === "repeater") {
    return (
      <PublicFormRepeaterBlock
        field={field}
        rows={repeaterVals[field.id] ?? []}
        disabled={disabled}
        fieldError={fieldError}
        onClearError={() => clearFieldError(field.id)}
        onChange={(rows) => {
          clearFieldError(field.id);
          setRepeaterVals((prev) => ({ ...prev, [field.id]: rows }));
        }}
      />
    );
  }

  return null;
}

export function buildAnswersPayload(
  detail: PublicFormResponseDTO,
  textVals: Record<string, string>,
  checkVals: Record<string, string[]>,
  fileVals: Record<string, string>,
  repeaterVals: Record<string, RepeaterAnswer>,
): {
  answers: Array<{ fieldId: string; value?: string; fileKey?: string }>;
} {
  const ordered = [...detail.fields].sort((a, b) => a.order - b.order);
  const answers: Array<{ fieldId: string; value?: string; fileKey?: string }> =
    [];

  for (const f of ordered) {
    if (f.type === "section") continue;
    if (f.type === "file") {
      const fk = fileVals[f.id]?.trim();
      if (!fk) continue;
      answers.push({ fieldId: f.id, fileKey: fk });
      continue;
    }
    if (f.type === "checkbox") {
      const arr = checkVals[f.id] ?? [];
      if (arr.length === 0) continue;
      answers.push({ fieldId: f.id, value: JSON.stringify(arr) });
      continue;
    }
    if (f.type === "repeater") {
      const rows = repeaterVals[f.id] ?? [];
      if (rows.length === 0) continue;
      answers.push({
        fieldId: f.id,
        value: serializeRepeaterAnswer(rows),
      });
      continue;
    }
    const v = (textVals[f.id] ?? "").trim();
    if (!v) continue;
    answers.push({ fieldId: f.id, value: v });
  }

  return { answers };
}

export function collectClientFieldErrors(
  ordered: PublicFormFieldDTO[],
  textVals: Record<string, string>,
  checkVals: Record<string, string[]>,
  fileVals: Record<string, string>,
  repeaterVals: Record<string, RepeaterAnswer>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of ordered) {
    if (f.type === "section") continue;
    if (f.type === "number") {
      const msg = publicClientNumberFieldMessage(
        textVals[f.id] ?? "",
        f.settings,
        f.required,
      );
      if (msg) out[f.id] = msg;
      continue;
    }
    if (f.type === "repeater") {
      const settings = parseRepeaterSettings(f.settings);
      if (settings) {
        const msg = getRepeaterClientError(
          repeaterVals[f.id] ?? [],
          settings,
          f.required,
        );
        if (msg) out[f.id] = msg;
      }
      continue;
    }
    if (!f.required) continue;
    if (f.type === "file") {
      if (!(fileVals[f.id] ?? "").trim()) {
        out[f.id] = "ກະລຸນາແນບໄຟລ";
      }
      continue;
    }
    if (f.type === "checkbox") {
      if ((checkVals[f.id] ?? []).length === 0) {
        out[f.id] = "ກະລຸນາເລືອກຢ່າງໜ້ອຍໜຶ່ງຕົວເລືອກ";
      }
      continue;
    }
    if (!(textVals[f.id] ?? "").trim()) {
      out[f.id] = "ກະລຸນາກອກຂໍ້ມູນ";
    }
  }
  return out;
}

function LoadErrorPane({ message }: { message: string }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center p-6">
      <Card className="w-full max-w-lg border-destructive/40">
        <CardContent className="space-y-2 pt-6">
          <h1 className="font-semibold text-lg">{message}</h1>
          <p className="text-muted-foreground text-sm">
            ຖ້າຕ້ອງການຄວາມຊ່ວຍເຫຼືອ ກະລຸນາຕິດຕໍ່ຜູ້ຈັດ ຫຼື ເຈົ້າໜ້າທີ່ທີ່ກ່ຽວຂ້ອງ
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function PublicFormPage() {
  const { formId } = useParams({ from: "/f/$formId" });
  const resolvedId =
    typeof formId === "string" && formId.length > 0 ? formId : "";
  const navigate = useNavigate();

  const [textVals, setTextVals] = useState<Record<string, string>>({});
  const [checkVals, setCheckVals] = useState<Record<string, string[]>>({});
  const [fileVals, setFileVals] = useState<Record<string, string>>({});
  const [fileBusy, setFileBusy] = useState<Record<string, boolean>>({});
  const [repeaterVals, setRepeaterVals] = useState<
    Record<string, RepeaterAnswer>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = useCallback((fieldId: string) => {
    setFieldErrors((prev) => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const setFieldErrorOne = useCallback((fieldId: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [fieldId]: message }));
  }, []);

  const query = useQuery({
    queryKey: ["public-forms", resolvedId],
    queryFn: () => publicFormsApi.get(resolvedId),
    enabled: resolvedId.length > 0,
  });

  const detail = query.data;

  useEffect(() => {
    if (!detail) return;
    const nextCheck: Record<string, string[]> = {};
    for (const f of detail.fields) {
      if (f.type === "checkbox") nextCheck[f.id] = [];
    }
    setCheckVals(nextCheck);
    setTextVals({});
    setFileVals({});
    setRepeaterVals(initRepeaterValsForFields(detail.fields));
    setFieldErrors({});
  }, [detail]);

  const orderedFields = useMemo(
    () => [...(detail?.fields ?? [])].sort((a, b) => a.order - b.order),
    [detail?.fields],
  );

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!detail) throw new Error("NO_FORM");
      const payload = buildAnswersPayload(
        detail,
        textVals,
        checkVals,
        fileVals,
        repeaterVals,
      );
      return publicFormsApi.submit(detail.id, payload);
    },
    onSuccess: async () => {
      if (!detail) return;
      toast.success("ສົ່ງຟອມສຳເລັດ");
      await navigate({
        to: "/f/$formId/success",
        params: { formId: detail.id },
      });
    },
    onError: (e) => {
      const err = e as unknown as PublicFormHttpError;
      if (err.fieldId) {
        setFieldErrors({
          [err.fieldId]: publicSubmitFieldMessage(err.code),
        });
        return;
      }
      const { title, detail: detailText } = describePublicSubmitError(err.code);
      toast.error(title, detailText ? { description: detailText } : undefined);
    },
  });

  if (!resolvedId) {
    return <LoadErrorPane message="ລິ້ງຟອມບໍ່ຖືກຕ້ອງ ຫຼື ບໍ່ພົບຟອມ" />;
  }

  if (query.isLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader />
        <span className="text-sm">ກຳລັງໂຫຼດຟອມ…</span>
      </div>
    );
  }

  if (query.isError) {
    const err = query.error as unknown as PublicFormHttpError;
    return (
      <LoadErrorPane message={describePublicLoadError(err.code ?? "UNKNOWN")} />
    );
  }

  if (!detail) return null;

  const canSubmit = detail.submission.open;
  const deadLabel =
    detail.settings.deadline != null
      ? new Date(detail.settings.deadline).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : null;

  const closedBanner =
    !canSubmit && detail.submission.open === false
      ? detail.submission.reason === "FORM_FULL"
        ? "ຟອມນີ້ຮັບຄຳຕອບຄົບຈຳນວນແລ້ວ ບໍ່ສາມາດສົ່ງເພີ່ມໃນເວລານີ້"
        : "ຟອມນີ້ບໍ່ເປີດຮັບຄຳຕອບໃນຕອນນີ້"
      : null;

  const runSubmit = () => {
    setFieldErrors({});
    const clientErrs = collectClientFieldErrors(
      orderedFields,
      textVals,
      checkVals,
      fileVals,
      repeaterVals,
    );
    if (Object.keys(clientErrs).length > 0) {
      setFieldErrors(clientErrs);
      return;
    }
    submitMut.mutate();
  };

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-8 px-4 py-8 pb-16">
      {detail.settings.posterUrl ? (
        <div className="overflow-hidden rounded-md border border-border bg-muted">
          <img
            src={detail.settings.posterUrl}
            alt=""
            className="h-full w-full object-contain"
          />
        </div>
      ) : null}

      <header className="space-y-2">
        <h1 className="font-semibold text-2xl tracking-tight">
          {detail.title}
        </h1>
        {detail.description ? (
          <p className="text-muted-foreground text-sm leading-relaxed">
            {detail.description}
          </p>
        ) : null}
        <p className="text-muted-foreground text-xs">{config.brandName}</p>
      </header>

      {closedBanner ? (
        <Card className="border-muted bg-muted/30">
          <CardContent className="text-sm">{closedBanner}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-6">
          {deadLabel ? (
            <p className="text-muted-foreground text-xs">
              ປິດຮັບເມື່ອເຖິງກຳນົດ: {deadLabel}
            </p>
          ) : null}

          <div className="space-y-6">
            {orderedFields.map((f) => (
              <PublicFormFieldBlock
                key={f.id}
                formId={detail.id}
                field={f}
                disabled={!canSubmit || submitMut.isPending}
                fieldError={fieldErrors[f.id]}
                clearFieldError={clearFieldError}
                setFieldError={setFieldErrorOne}
                textVals={textVals}
                setTextVals={setTextVals}
                checkVals={checkVals}
                setCheckVals={setCheckVals}
                fileVals={fileVals}
                setFileVals={setFileVals}
                fileBusy={fileBusy}
                setFileBusy={setFileBusy}
                repeaterVals={repeaterVals}
                setRepeaterVals={setRepeaterVals}
              />
            ))}
          </div>

          <Button
            type="button"
            className="w-full sm:w-auto"
            disabled={!canSubmit || submitMut.isPending}
            onClick={runSubmit}
          >
            {submitMut.isPending ? "ກຳລັງສົ່ງ…" : "ສົ່ງຟອມ"}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
